// Tableaux de signes et de variations « ⊞{…} » (lot I9) — format publié de l'ancien moteur (kernel-signs.ts,
// kernel-variations.ts), mêmes messages, mêmes HORA, mêmes affirmations ; calcul propre au moteur v2.
//
// Une ligne « tableau » est UN bout : ⊞{"v":[valeurs de x],"r":[[étiquette,"cases"]],"f":[étiquette,[cases]]}.
//  - « v » : valeurs de x écrites (sans ±∞), 6 au plus ;
//  - « r » : une ligne par facteur, puis l'expression étudiée ; 2n + 1 cases qui alternent intervalle / valeur :
//    « + », « − », « 0 », « ‖ » (valeur interdite), « . » (vide) ;
//  - « f » (tableau de variations) : la ligne de f — flèche « ↗ » / « ↘ » sur un intervalle ; sous une valeur, la
//    valeur de f, « ‖ » ou rien.
// Le sujet (l'expression étudiée) est découpé en facteurs dont le signe se lit : constantes, exponentielles,
// polynômes (racines exactes au 1er et au 2nd degré, approchées au-delà), puissances entières de ces facteurs.
// Les valeurs et les signes se comparent en valeurs approchées (tolérance 1e-9).
import { parse } from './syntax.js';
import { evaluate, toF } from './equiv.js';
import { symbols } from './ast.js';

export const SIGN_TABLE_MARK = '⊞';
const MAX_VALUES = 6;
const MAX_ROWS = 6;
const EPS = 1e-9;
const ARROWS = ['↗', '↘'];
const CELL_OF = { '+': '+', '−': '−', '-': '−', 0: '0', '‖': '‖', '.': '' };

export const isSignTableText = (text) => String(text ?? '').startsWith(SIGN_TABLE_MARK);

/** Lit une ligne « tableau » ; null si elle est mal formée. */
export function parseSignTable(text) {
  if (!isSignTableText(text)) return null;
  let raw;
  try {
    raw = JSON.parse(String(text).slice(SIGN_TABLE_MARK.length));
  } catch {
    return null;
  }
  if (!raw || typeof raw !== 'object') return null;
  const { v, r, f } = raw;
  if (!Array.isArray(v) || !Array.isArray(r)) return null;
  if (v.length > MAX_VALUES || !r.length || r.length > MAX_ROWS) return null;
  if (!v.every((s) => typeof s === 'string' && s.trim() && s.length <= 40)) return null;
  const n = 2 * v.length + 1;
  const rows = [];
  for (const row of r) {
    if (!Array.isArray(row) || row.length !== 2) return null;
    const [label, cells] = row;
    if (typeof label !== 'string' || !label.trim() || label.length > 80) return null;
    if (typeof cells !== 'string') return null;
    const chars = Array.from(cells);
    if (chars.length !== n || !chars.every((c) => c in CELL_OF)) return null;
    rows.push({ label, cells: chars.map((c) => CELL_OF[c]) });
  }
  if (f === undefined) return { values: v, rows };
  if (!Array.isArray(f) || f.length !== 2) return null;
  const [flabel, fcells] = f;
  if (typeof flabel !== 'string' || !flabel.trim() || flabel.length > 80) return null;
  if (!Array.isArray(fcells) || fcells.length !== n) return null;
  const cells = [];
  for (let i = 0; i < n; i++) {
    const c = fcells[i];
    if (typeof c !== 'string' || c.length > 40) return null;
    if (i % 2 === 0 && c !== '' && !ARROWS.includes(c)) return null;
    cells.push(c.trim());
  }
  return { values: v, rows, f: { label: flabel, cells } };
}

// ── Valeurs ────────────────────────────────────────────────────────────────
const fval = (ast, env, table) => {
  try {
    const v = toF(evaluate(ast, env, table));
    return typeof v === 'number' ? v : NaN;
  } catch {
    return NaN;
  }
};
const same = (a, b) => Math.abs(a - b) <= EPS * Math.max(1, Math.abs(a), Math.abs(b));
const hasVar = (ast, x) => symbols(ast).has(x);

/** Un nombre écrit (« −1 ÷ exp(1) », « 0,5 ») → sa valeur ; NaN s'il ne se lit pas. */
export function readNumber(text, table) {
  try {
    const a = parse(String(text), table);
    if (symbols(a).size) return NaN;
    return fval(a, {}, table);
  } catch {
    return NaN;
  }
}

// Coefficients (approchés) d'un polynôme en x de degré ≤ 6, par interpolation ; null si ce n'en est pas un.
function polyCoeffs(ast, x, table) {
  const N = 7;
  const xs = Array.from({ length: N }, (_, i) => i - 3);
  const ys = xs.map((t) => fval(ast, { [x]: t }, table));
  if (ys.some((y) => !Number.isFinite(y))) return null;
  // différences divisées → forme de Newton → coefficients
  const dd = ys.slice(),
    newton = [dd[0]];
  for (let j = 1; j < N; j++) {
    for (let i = N - 1; i >= j; i--) dd[i] = (dd[i] - dd[i - 1]) / (xs[i] - xs[i - j]);
    newton.push(dd[j]);
  }
  let c = [0];
  for (let j = N - 1; j >= 0; j--) {
    const next = new Array(c.length + 1).fill(0);
    c.forEach((cj, i) => {
      next[i + 1] += cj;
      next[i] -= cj * xs[j];
    });
    next[0] += newton[j];
    c = next;
  }
  const scale = Math.max(1, ...c.map(Math.abs));
  c = c.map((k) => (Math.abs(k) < 1e-9 * scale ? 0 : k));
  while (c.length > 1 && c[c.length - 1] === 0) c.pop();
  // contrôle hors des points d'interpolation
  for (const t of [0.37, -2.6, 4.3]) {
    const want = fval(ast, { [x]: t }, table);
    const got = c.reduceRight((acc, k) => acc * t + k, 0);
    if (!Number.isFinite(want) || !same(want, got)) return null;
  }
  return c;
}

const peval = (c, t) => c.reduceRight((acc, k) => acc * t + k, 0);
const pderive = (c) => c.slice(1).map((k, i) => k * (i + 1));

/** Racines réelles d'un polynôme (sans multiplicité), triées. */
function polyRoots(c) {
  const d = c.length - 1;
  if (d <= 0) return [];
  if (d === 1) return [-c[0] / c[1]];
  if (d === 2) {
    const [C, B, A] = c;
    const D = B * B - 4 * A * C;
    if (D < -1e-12 * Math.max(1, B * B)) return [];
    if (Math.abs(D) <= 1e-12 * Math.max(1, B * B)) return [-B / (2 * A)];
    const s = Math.sqrt(D);
    return [(-B - s) / (2 * A), (-B + s) / (2 * A)].sort((a, b) => a - b);
  }
  // degré ≥ 3 : racines de la dérivée (extremums) → intervalles monotones → dichotomie ; plus les racines doubles
  const crit = polyRoots(pderive(c));
  const bound = 1 + Math.max(...c.slice(0, -1).map((k) => Math.abs(k / c[d])));
  const pts = [-bound, ...crit, bound];
  const out = [];
  const push = (r) => {
    if (!out.some((o) => same(o, r))) out.push(r);
  };
  for (const r of crit) if (Math.abs(peval(c, r)) <= 1e-9 * Math.max(1, Math.abs(c[d]))) push(r);
  for (let i = 0; i + 1 < pts.length; i++) {
    let a = pts[i],
      b = pts[i + 1];
    let fa = peval(c, a);
    const fb = peval(c, b);
    if (fa === 0) push(a);
    if (fa * fb >= 0) continue;
    for (let k = 0; k < 200; k++) {
      const m = (a + b) / 2,
        fm = peval(c, m);
      if (fa * fm <= 0) b = m;
      else {
        a = m;
        fa = fm;
      }
    }
    push((a + b) / 2);
  }
  return out.sort((a, b) => a - b);
}

/** Valeur arrondie au plus juste pour l'écrire (« 0,5 », « −2 ») ; une valeur irrationnelle garde 4 décimales. */
function valueText(v) {
  for (let q = 1; q <= 12; q++) {
    const p = Math.round(v * q);
    if (Math.abs(p / q - v) < 1e-9) {
      if (q === 1) return String(p).replace('-', '−');
      const dec = p / q;
      if (Math.abs(Math.round(dec * 1e4) / 1e4 - dec) < 1e-12)
        return String(dec).replace('.', ',').replace('-', '−');
      return `${p < 0 ? '−' : ''}${Math.abs(p)}/${q}`;
    }
  }
  return String(Math.round(v * 1e4) / 1e4)
    .replace('.', ',')
    .replace('-', '−');
}

/**
 * Facteurs du sujet dont le signe se lit ; null sinon.
 * Facteur : { ast, den, roots: [valeurs où il s'annule], constant }
 */
function collect(ast, x, table, den, out) {
  if (!hasVar(ast, x)) {
    const v = fval(ast, {}, table);
    if (!Number.isFinite(v) || Math.abs(v) < 1e-12) return false;
    if (v < 0) out.push({ ast, den, roots: [], constant: true });
    return true;
  }
  const op = table.nodes[ast.t]?.eval;
  if (op === 'mul') return ast.k.every((c) => collect(c, x, table, den, out));
  if (op === 'div')
    return collect(ast.k[0], x, table, den, out) && collect(ast.k[1], x, table, !den, out);
  if (op === 'neg') {
    out.push({ ast: { t: 'Num', p: -1, q: 1 }, den: false, roots: [], constant: true });
    return collect(ast.k[0], x, table, den, out);
  }
  if (op === 'call' && ast.name === 'exp') {
    out.push({ ast, den, roots: [], constant: false });
    return true;
  }
  if (op === 'pow') {
    const [b, e] = ast.k;
    if (e.t !== 'Num' || e.q !== 1 || e.p < 1 || hasVar(e, x)) return false;
    if (b.t === 'Call' && b.name === 'exp') {
      out.push({ ast, den, roots: [], constant: false });
      return true;
    }
    const c = polyCoeffs(b, x, table);
    if (!c) return false;
    out.push({ ast, den, roots: polyRoots(c), constant: false });
    return true;
  }
  const c = polyCoeffs(ast, x, table);
  if (!c) return false;
  out.push({ ast, den, roots: polyRoots(c), constant: false });
  return true;
}

/**
 * Le tableau de signes attendu pour l'expression `subject` (en x) ; null si un facteur n'a pas de signe connu
 * ou si le tableau serait trop grand.
 */
export function expectedSignTable(subject, x, table) {
  if (!hasVar(subject, x)) return null;
  const factors = [];
  if (!collect(subject, x, table, false, factors)) return null;
  if (!factors.some((f) => !f.constant)) return null;
  const values = [];
  for (const f of factors)
    for (const r of f.roots) if (!values.some((v) => same(v, r))) values.push(r);
  values.sort((a, b) => a - b);
  if (values.length > MAX_VALUES) return null;
  const pts = [];
  if (!values.length) pts.push(0);
  else {
    pts.push(values[0] - 1);
    for (let i = 0; i + 1 < values.length; i++) pts.push((values[i] + values[i + 1]) / 2);
    pts.push(values.at(-1) + 1);
  }
  const signAt = (p) => {
    const v = fval(subject, { [x]: p }, table);
    if (!Number.isFinite(v) || Math.abs(v) < 1e-12) return null;
    return v > 0 ? '+' : '−';
  };
  const last = [];
  for (let i = 0; i < pts.length; i++) {
    const s = signAt(pts[i]);
    if (!s) return null;
    last.push(s);
    if (i < values.length) {
      const v = values[i];
      const isRoot = (f) => f.roots.some((r) => same(r, v));
      last.push(
        factors.some((f) => f.den && isRoot(f))
          ? '‖'
          : factors.some((f) => !f.den && isRoot(f))
            ? '0'
            : '',
      );
    }
  }
  return {
    x,
    subject,
    values,
    valueTexts: values.map(valueText),
    pts,
    last,
    lone: factors.length === 1,
    factorCount: factors.length,
  };
}

// ── Vérification d'un tableau de signes ──────────────────────────────────────
function intervalText(exp, i) {
  const v = exp.valueTexts;
  return `]${i === 0 ? '−∞' : v[i - 1]} ; ${i === v.length ? '+∞' : v[i]}[`;
}
const norm = (s) => String(s).replace(/\s+/g, '').replace(/-/g, '−').replace(/'/g, '′');

/**
 * Les étiquettes : la dernière ligne est le sujet (son nom `subjectName`, ou une écriture de même valeur), les
 * autres des facteurs dont le produit (certains au dénominateur) redonne le sujet. → [ast par ligne] | null
 */
function readRows(exp, t, table, subjectName) {
  const x = exp.x;
  const asts = [];
  for (let r = 0; r < t.rows.length; r++) {
    const label = t.rows[r].label;
    const isLast = r === t.rows.length - 1;
    if (isLast && subjectName && norm(label) === norm(subjectName)) {
      asts.push(exp.subject);
      continue;
    }
    let a;
    try {
      a = parse(label, table);
    } catch {
      return null;
    }
    if ([...symbols(a)].some((s) => s !== x)) return null;
    asts.push(a);
  }
  const probes = [-2.37, -0.61, 0.43, 1.79, 3.13];
  const at = (a, p) => fval(a, { [x]: p }, table);
  // dernière ligne = le sujet
  const lastAst = asts.at(-1);
  if (
    !probes.every((p) => {
      const a = at(lastAst, p),
        b = at(exp.subject, p);
      return !Number.isFinite(b) || same(a, b);
    })
  )
    return null;
  const factors = asts.slice(0, -1);
  if (!factors.length) return exp.lone ? asts : null;
  // produit des facteurs (chaque facteur au numérateur ou au dénominateur) = le sujet
  const k = factors.length;
  for (let mask = 0; mask < 1 << k; mask++) {
    const ok = probes.every((p) => {
      const want = at(exp.subject, p);
      if (!Number.isFinite(want)) return true;
      let prod = 1;
      for (let i = 0; i < k; i++) {
        const v = at(factors[i], p);
        prod = mask & (1 << i) ? prod / v : prod * v;
      }
      return same(prod, want);
    });
    if (ok) return asts;
  }
  return null;
}

/**
 * La première erreur du tableau de l'élève (sans la ligne de f) ; null s'il est juste.
 * → { title, message, row?, col? }
 */
export function checkSignTable(exp, t, table, subjectName) {
  const x = exp.x;
  const subj = subjectName ?? 'l’expression';
  const asts = readRows(exp, t, table, subjectName);
  if (!asts)
    return {
      title: 'Ce tableau ne va pas avec ta ligne',
      message: `Les lignes du tableau sont les facteurs de ta dernière ligne comparée à 0, puis ${subj}. Ouvre le tableau depuis la palette.`,
    };
  // 1. la ligne des x
  const got = [];
  for (let i = 0; i < t.values.length; i++) {
    const v = readNumber(t.values[i], table);
    if (!Number.isFinite(v))
      return {
        title: 'Valeur illisible',
        message: `Je ne lis pas « ${t.values[i]} » comme un nombre.`,
        row: -1,
        col: i,
      };
    if (!exp.values.some((e) => same(e, v)))
      return {
        title: 'Une valeur en trop',
        message: `Aucun facteur ne s’annule en ${x} = ${t.values[i]} : retire cette valeur.`,
        row: -1,
        col: i,
      };
    if (got.some((g) => same(g, v)))
      return {
        title: 'Une valeur deux fois',
        message: `${x} = ${t.values[i]} est déjà sur la ligne des ${x}.`,
        row: -1,
        col: i,
      };
    got.push(v);
  }
  if (got.length < exp.values.length)
    return {
      title: 'Il manque une valeur',
      message: `Chaque facteur s’annule pour une valeur de ${x} : cherche-les toutes et place-les sur la ligne des ${x}.`,
      row: -1,
    };
  for (let i = 0; i < got.length; i++)
    if (!same(got[i], exp.values[i]))
      return {
        title: 'Dans l’ordre croissant',
        message: `Range les valeurs de ${x} de la plus petite à la plus grande, de −∞ à +∞.`,
        row: -1,
        col: i,
      };
  // 2. les cases, ligne par ligne
  const lastRow = t.rows.length - 1;
  for (let r = 0; r <= lastRow; r++) {
    const a = asts[r];
    const label = t.rows[r].label;
    const want = [];
    if (r === lastRow) want.push(...exp.last);
    else
      for (let i = 0; i < exp.pts.length; i++) {
        const s = fval(a, { [x]: exp.pts[i] }, table);
        want.push(s > 0 ? '+' : '−');
        if (i < exp.values.length) {
          const z = fval(a, { [x]: exp.values[i] }, table);
          want.push(Number.isFinite(z) && Math.abs(z) < 1e-9 ? '0' : '');
        }
      }
    const cells = t.rows[r].cells;
    for (let c = 0; c < want.length; c++) {
      const w = want[c],
        have = cells[c];
      if (w === have) continue;
      const i = c >> 1;
      if (c % 2 === 0) {
        if (!have)
          return {
            title: 'Il manque un signe',
            message: `Mets un signe (+ ou −) sur chaque intervalle : il en manque un sur ${intervalText(exp, i)}.`,
            row: r,
            col: c,
          };
        return {
          title: 'Pas le bon signe',
          message:
            r === lastRow && lastRow > 0
              ? `Sur ${intervalText(exp, i)}, applique la règle des signes aux facteurs de la colonne.`
              : `Le signe de ${label} sur ${intervalText(exp, i)} n’est pas le bon : remplace ${x} par un nombre de cet intervalle.`,
          row: r,
          col: c,
        };
      }
      const v = exp.valueTexts[i];
      if (have === '‖' && r !== lastRow)
        return {
          title: 'La double barre',
          message:
            'La double barre ne va que sur la dernière ligne, sous une valeur interdite (le dénominateur s’annule). Sur la ligne d’un facteur, écris 0 là où il s’annule.',
          row: r,
          col: c,
        };
      if (w === '‖')
        return {
          title: 'Une valeur interdite',
          message: `En ${x} = ${v}, le dénominateur s’annule : la valeur est interdite, mets une double barre ‖.`,
          row: r,
          col: c,
        };
      if (w === '0')
        return {
          title: 'Ici, ça s’annule',
          message:
            r === lastRow
              ? `En ${x} = ${v}, un facteur du numérateur s’annule : l’expression vaut 0.`
              : `${label} s’annule en ${x} = ${v} : écris 0.`,
          row: r,
          col: c,
        };
      return {
        title: 'Rien ne s’annule ici',
        message:
          r === lastRow
            ? `En ${x} = ${v}, rien ne s’annule : laisse la case vide.`
            : `${label} ne s’annule pas en ${x} = ${v} : laisse la case vide.`,
        row: r,
        col: c,
      };
    }
  }
  return null;
}

// ── Tableau de variations ─────────────────────────────────────────────────
/**
 * Le tableau de variations attendu pour f′ = `fprime` : signes de f′, flèches, valeurs interdites, valeurs de f
 * (si f est connue, `fExpr`). null si le signe de f′ ne se lit pas.
 */
export function expectedVarTable(fprime, x, table, fExpr) {
  const sign = expectedSignTable(fprime, x, table);
  if (!sign) return null;
  const n = sign.values.length;
  const signs = [];
  for (let i = 0; i <= n; i++) signs.push(sign.last[2 * i]);
  const forbidden = sign.values.map((_, i) => sign.last[2 * i + 1] === '‖');
  const values = sign.values.map((v, i) => {
    if (forbidden[i] || !fExpr) return null;
    const fv = fval(fExpr, { [x]: v }, table);
    return Number.isFinite(fv) ? fv : null;
  });
  return { sign, signs, arrows: signs.map((s) => (s === '+' ? '↗' : '↘')), forbidden, values };
}

/** La première erreur de la ligne de f ; null si elle est juste. */
export function checkVarRow(exp, t, table, { f, x }) {
  const fRow = t.rows.length;
  if (!t.f)
    return {
      title: `Il manque la ligne de ${f}`,
      message: `Sous la ligne de ${f}′(${x}), écris les flèches de ${f} : ↗ là où ${f}′ est positive, ↘ là où elle est négative.`,
      row: fRow,
    };
  if (norm(t.f.label) !== norm(`${f}(${x})`) && norm(t.f.label) !== norm(f))
    return {
      title: 'La dernière ligne',
      message: `La dernière ligne du tableau est celle de ${f} : ouvre le tableau depuis la palette.`,
    };
  const n = exp.sign.values.length;
  for (let c = 0; c < 2 * n + 1; c++) {
    const have = t.f.cells[c] ?? '';
    const i = c >> 1;
    if (c % 2 === 0) {
      const want = exp.arrows[i];
      if (have === want) continue;
      if (!have)
        return {
          title: 'Il manque une flèche',
          message: `Mets une flèche sur chaque intervalle : il en manque une sur ${intervalText(exp.sign, i)}.`,
          row: fRow,
          col: c,
        };
      return {
        title: 'Pas le bon sens de variation',
        message: `Sur ${intervalText(exp.sign, i)}, la flèche suit le signe de ${f}′ : positive, ${f} croît (↗) ; négative, ${f} décroît (↘).`,
        row: fRow,
        col: c,
      };
    }
    const vx = exp.sign.valueTexts[i];
    if (exp.forbidden[i]) {
      if (have === '‖') continue;
      return {
        title: 'Une valeur interdite',
        message: `${f} n’est pas définie en ${x} = ${vx} : mets une double barre ‖ sous cette valeur.`,
        row: fRow,
        col: c,
      };
    }
    if (have === '‖')
      return {
        title: 'La double barre',
        message: `${f} est définie en ${x} = ${vx} : la double barre ne va que sous une valeur interdite.`,
        row: fRow,
        col: c,
      };
    const want = exp.values[i];
    if (want === null) {
      if (!have) continue;
      return {
        title: `On ne connaît pas ${f}`,
        message: `L’énoncé ne donne que ${f}′ : laisse vide la case sous ${x} = ${vx}.`,
        row: fRow,
        col: c,
      };
    }
    if (!have)
      return {
        title: `La valeur de ${f}`,
        message: `Sous ${x} = ${vx}, écris ${f}(${vx}) : remplace ${x} par ${vx} dans ${f}(${x}).`,
        row: fRow,
        col: c,
      };
    const got = readNumber(have, table);
    if (!Number.isFinite(got) || !same(got, want))
      return {
        title: 'Pas la bonne valeur',
        message: `${f}(${vx}) ne vaut pas ${have} : remplace ${x} par ${vx} dans ${f}(${x}) et calcule.`,
        row: fRow,
        col: c,
      };
  }
  return null;
}

// ── Vérité des affirmations (kernel.vclaims) ─────────────────────────────────
const WHOLE = { lo: -Infinity, hi: Infinity, loC: false, hiC: false };
const FLIP = { '<': '>', '>': '<', '≤': '≥', '≥': '≤' };

/** « −2 ≤ x ≤ 1 », « x ≤ 3 », « x > −3 » → un morceau ; null sinon. */
export function pieceOf(text, x, table) {
  const parts = String(text).split(/\s*([<>≤≥])\s*/u);
  if (parts.length !== 3 && parts.length !== 5) return null;
  const piece = { ...WHOLE };
  for (let i = 0; i + 2 < parts.length; i += 2) {
    const [l, rel0, r] = [parts[i].trim(), parts[i + 1], parts[i + 2].trim()];
    const xl = l === x,
      xr = r === x;
    if (xl === xr) return null;
    const c = readNumber(xl ? r : l, table);
    if (!Number.isFinite(c)) return null;
    const rel = xl ? rel0 : FLIP[rel0];
    if (rel === '<' || rel === '≤') {
      piece.hi = c;
      piece.hiC = rel === '≤';
    } else {
      piece.lo = c;
      piece.loC = rel === '≥';
    }
  }
  return piece.lo < piece.hi ? piece : null;
}

function overlapping(t, p) {
  const out = [];
  const v = t.sign.values,
    n = v.length;
  for (let i = 0; i <= n; i++) {
    const lo = i === 0 ? -Infinity : v[i - 1];
    const hi = i === n ? Infinity : v[i];
    if (Math.max(lo, p.lo) < Math.min(hi, p.hi) - EPS) out.push(i);
  }
  return out;
}
const inPiece = (c, p) =>
  (c > p.lo + EPS || (p.loC && Math.abs(c - p.lo) <= EPS)) &&
  (c < p.hi - EPS || (p.hiC && Math.abs(c - p.hi) <= EPS));
const forbiddenIn = (t, p) => t.sign.values.some((c, i) => t.forbidden[i] && inPiece(c, p));
function monotone(t, p, dir) {
  const ov = overlapping(t, p);
  return !!ov.length && !forbiddenIn(t, p) && ov.every((i) => t.signs[i] === dir);
}
const valueIndex = (t, c) => t.sign.values.findIndex((v) => same(v, c));
function globalAt(t, c, D, kind) {
  if (!inPiece(c, D) || forbiddenIn(t, D)) return false;
  const left = { lo: D.lo, loC: D.loC, hi: c, hiC: false };
  const right = { lo: c, loC: false, hi: D.hi, hiC: D.hiC };
  const down = kind === 'min' ? '−' : '+';
  const up = kind === 'min' ? '+' : '−';
  return (
    (left.hi - left.lo <= EPS || monotone(t, left, down)) &&
    (right.hi - right.lo <= EPS || monotone(t, right, up))
  );
}
function localAt(t, k, kind) {
  if (k < 0 || t.forbidden[k]) return false;
  const [a, b] = [t.signs[k], t.signs[k + 1]];
  return kind === 'min' ? a === '−' && b === '+' : a === '+' && b === '−';
}
function globalPoints(t, D) {
  const out = t.sign.values.map((c) => ({ c, at: true }));
  if (D.loC && Number.isFinite(D.lo)) out.push({ c: D.lo, at: false });
  if (D.hiC && Number.isFinite(D.hi)) out.push({ c: D.hi, at: false });
  return out;
}

/** Vérité d'une affirmation, lue dans le tableau attendu ; null si elle ne s'y lit pas (`other`). */
export function claimTruth(t, c, { x, fExpr, table }) {
  const piece = (s) => {
    const p = pieceOf(s, x, table);
    if (!p) throw new Error(`intervalle illisible : « ${s} »`);
    return p;
  };
  const pieces = (c.on ?? []).map(piece);
  const D = pieces[0] ?? WHOLE;
  const at = (c.at ?? []).map((s) => readNumber(s, table));
  const value = c.value ? readNumber(c.value, table) : NaN;
  const valueOk = (where) => {
    if (!fExpr) return false;
    const fv = fval(fExpr, { [x]: where }, table);
    return Number.isFinite(fv) && same(fv, value);
  };
  switch (c.kind) {
    case 'other':
      return null;
    case 'constant':
      return false;
    case 'increasing':
    case 'decreasing': {
      const dir = c.kind === 'increasing' ? '+' : '−';
      return (pieces.length ? pieces : [WHOLE]).every((p) => monotone(t, p, dir));
    }
    case 'sign': {
      const ov = overlapping(t, D);
      if (c.sign === '0') return false;
      if (c.sign === 'change')
        return ov.some((i) => t.signs[i] === '+') && ov.some((i) => t.signs[i] === '−');
      return !!ov.length && ov.every((i) => t.signs[i] === c.sign);
    }
    case 'sequence': {
      const runs = [];
      for (const i of overlapping(t, D)) {
        const r = t.signs[i] === '+' ? 'increasing' : 'decreasing';
        if (runs.at(-1) !== r) runs.push(r);
      }
      return !forbiddenIn(t, D) && runs.join() === (c.seq ?? []).join();
    }
    case 'extrema': {
      const pts = t.sign.values.filter((_, k) => localAt(t, k, 'min') || localAt(t, k, 'max'));
      return pts.length === at.length && at.every((a) => pts.some((p) => same(p, a)));
    }
    case 'no_extremum': {
      const k = at.length ? valueIndex(t, at[0]) : -1;
      if (k < 0 || t.forbidden[k]) return false;
      return t.sign.last[2 * k + 1] === '0' && t.signs[k] === t.signs[k + 1];
    }
    case 'no_min':
    case 'no_max': {
      const kind = c.kind === 'no_min' ? 'min' : 'max';
      return !globalPoints(t, D).some((p) => globalAt(t, p.c, D, kind));
    }
    case 'min':
    case 'max': {
      const kind = c.kind;
      if (at.length) {
        const where = at[0];
        const k = valueIndex(t, where);
        const ok = c.local ? localAt(t, k, kind) && inPiece(where, D) : globalAt(t, where, D, kind);
        if (!ok) return false;
        return c.value ? valueOk(where) : true;
      }
      if (!c.value) return null;
      if (c.local)
        return t.sign.values.some((v, k) => localAt(t, k, kind) && inPiece(v, D) && valueOk(v));
      const best = globalPoints(t, D).find((p) => globalAt(t, p.c, D, kind));
      return best ? valueOk(best.c) : false;
    }
    default:
      return null;
  }
}

/** Décisions après le tableau ; avec `single`, une vraie tranche les autres (élimination). */
export function decisionsOf(truths, single) {
  if (single) {
    const trues = truths.filter((v) => v === true).length;
    if (trues === 1) return truths.map((v) => v === true);
    const open = truths.filter((v) => v === null).length;
    if (!trues && open === 1) return truths.map((v) => v === null);
  }
  return truths;
}
